import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRealtimeListRefresh } from "../realtime/RealtimeContext";
import { RealtimeEventTypes } from "../realtime/realtimeEventTypes";
import { areaKey, type QueryArea } from "./queryKeys";

/**
 * Verbindet Realtime-Ereignisse mit dem Query-Cache — an **einer** Stelle.
 *
 * Vorher trug jede Fläche ihren eigenen `reload()`-Rückruf in `useRealtimeListRefresh`; 15 Stellen
 * mit je eigener Verdrahtung. Hier steht stattdessen die Zuordnung „welches Ereignis betrifft
 * welchen Bereich", und der Cache erledigt den Rest: alles, was zu dem Bereich gehört, wird
 * ungültig und lädt nach, sobald es wieder angezeigt wird.
 *
 * Der Abgleich nach einem Verbindungsabriss steckt bereits in `useRealtimeListRefresh` — diese
 * Brücke erbt ihn damit für alle angeschlossenen Bereiche.
 */

/** Welche fachlichen Bereiche ein Ereignis berührt. */
const AREAS_BY_EVENT: Readonly<Record<string, readonly QueryArea[]>> = {
  [RealtimeEventTypes.AppointmentsChanged]: ["appointments", "persons"],
  [RealtimeEventTypes.PersonsChanged]: ["persons"],
  [RealtimeEventTypes.CheckinsChanged]: ["check-ins", "persons"],
  // Aufgaben entstehen u. a. aus E-Mail-Eingängen und Versand-Freigaben.
  [RealtimeEventTypes.InboxChanged]: ["communication", "tasks"],
  [RealtimeEventTypes.OutboundMessagesChanged]: ["communication", "tasks"],
  [RealtimeEventTypes.EmailQueueChanged]: ["communication"],
  [RealtimeEventTypes.SmsQueueChanged]: ["communication"],
  [RealtimeEventTypes.WhatsAppQueueChanged]: ["communication"],
  [RealtimeEventTypes.CommunicationExecutionsChanged]: ["communication"],
  [RealtimeEventTypes.TenantDataChanged]: [
    "check-ins",
    "open-items",
    "contributions",
    "payments",
    "sepa-runs",
    "reminders",
    "leads",
    "appointments",
    "persons",
    "documents",
    "tasks",
    "lookups"
  ]
};

/** Liefert die Ereignistypen, die einen Bereich betreffen. */
export function eventTypesForArea(area: QueryArea): string[] {
  return Object.entries(AREAS_BY_EVENT)
    .filter(([, areas]) => areas.includes(area))
    .map(([type]) => type);
}

/**
 * Macht die Queries eines Bereichs ungültig, sobald ein passendes Ereignis eintrifft oder die
 * Verbindung nach einem Abriss zurückkehrt.
 */
export function useRealtimeQueryBridge(area: QueryArea, options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const eventTypes = React.useMemo(() => eventTypesForArea(area), [area]);

  const invalidate = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: areaKey(area) });
  }, [queryClient, area]);

  useRealtimeListRefresh({
    eventTypes,
    reload: invalidate,
    enabled: options?.enabled !== false
  });
}
